
import {
  defineComponent,
  ref,
  shallowRef,
  onMounted,
  computed,
  nextTick,
  watch,
} from "vue";
import { LookupAPI } from "@/services/api/Lookup.service";
import { ILookup } from "@/interfaces/iLookup";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import VZSideDrawer from "@/components/content/VZSideDrawer.vue";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { usePagination } from "@/helpers/pagination";
import LookupDetail from "@/views/lookup/views/LookupDetail.vue";
import LookupDataModel from "@/views/lookup/views/LookupDatamodel.vue";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { DrawerComponent } from "@/assets/ts/components";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import * as Yup from "yup";
import { useToast } from "vue-toastification";
import {
  lookupTableBulkDelete,
  lookupTableFilters,
  lookupTableHeaders,
} from "./dataTableOptions";
import { Modal } from "bootstrap";
import { hasPermission } from "@/core/services/permission.service";
import LookupCloneModal from "@/components/modals/forms/LookupCloneModal.vue";
export default defineComponent({
  name: "lookup-index",
  // eslint-disable-next-line vue/no-unused-components
  components: {
    Datatable,
    "vz-lookup-clone-modal": LookupCloneModal,
    VZSideDrawer,
    // eslint-disable-next-line vue/no-unused-components
    LookupDetail,
    // eslint-disable-next-line vue/no-unused-components
    LookupDataModel,
    VZDeleteModal,
  },
  setup() {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const datatable = ref<null | HTMLFormElement>(null);
    const toast = useToast();
    const addMessage = t("pages.lookup.lookupAddedMessage");
    const editMessage = t("pages.lookup.lookupUpdatedMessage");
    const { page, total, rowsPerPage } = usePagination();
    const loading = ref(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showDeleteDialog = ref<any>(false);
    const showSearch = true;
    const sideDrawerTitle = ref<string>("");
    const sideDrawerIcon = ref<string>("");
    const openDrawer = ref<boolean>(false);
    const tableData = ref<ILookup[]>([]);
    const selectedForAction = ref<Partial<ILookup>>({});
    const bulkDelete = ref<IBulkDelete>(lookupTableBulkDelete());
    const tableHeader = ref(lookupTableHeaders());
    const tableFilter = ref(lookupTableFilters());
    const lookupWebId = ref<any>();
    const openCloneModal = ref(false);
    let lookupObj = ref<any>({});

    const tabs = shallowRef<ITabConfig[]>([
      {
        title: t("pages.lookup.lookupDetail"),
        name: "lookup.detail",
        icon: "bi bi-card-text me-2 fs-4 text-primary",
        component: LookupDetail,
        props: {
          data: {},
        },
      },
      {
        title: t("pages.lookup.lookupDataModel"),
        name: "lookup.data.model",
        icon: "bi bi-box me-2 fs-4 text-primary",
        component: LookupDataModel,
        props: {
          data: {},
        },
      },
    ]);

    let formData = ref<IAddEditFormModel>({
      header: "Lookup",
      edit: false,
      data: null,
      defaultValues: {
        status: "Active",
      },
      label: [
        { addHeader: t("pages.lookup.addLookup") },
        { editHeader: t("pages.lookup.editLookup") },
      ],
      validationSchema: Yup.object({
        lookupName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/^LK[a-zA-Z0-9]+\S/, t("pages.lookup.lookupNameValidation")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .min(Yup.ref("startDate"), "End date can't be before Start date"),
        status: Yup.string().required(
          t("common.status") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "lookupName",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "startDate",
          label: t("common.startDate"),
          type: "date",
          required: true,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "endDate",
          label: t("common.endDate"),
          type: "date",
          required: false,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "status",
          label: t("common.status"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Please select status",
          options: [
            {
              label: t("common.active"),
              value: "Active",
            },
            {
              label: t("common.inactive"),
              value: "Inactive",
            },
          ],
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:CREATE",
      delete: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:DELETE",
      update: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:UPDATE",
    });
    const localPreference = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const tableRowClick = (item) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { ...tab.props, data: item } };
      });
      sideDrawerTitle.value = item.lookupName;
      sideDrawerIcon.value = "las la-door-open";
      DrawerComponent?.getInstance("vz__drawer")?.toggle();
    };
    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.lookup.title"), ["Home", "Lookups"]);
      loading.value = true;
      // queryUpdate({
      //   ...defaultFilter.value,
      //   page: page.value - 1,
      //   size: rowsPerPage.value,
      // });
    });
    const queryUpdate = async (params?: any) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
            startDate: params?.startDate === null ? [] : params?.startDate,
          };
        }
        localPreference.value = { ...params };
        const data = await LookupAPI.getInstance().getLookupList({
          ...params,
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };

    const editLookup = (item: ILookup) => {
      let existingElement = document.querySelector(
        ".lookup-clone.show-clone-" + lookupWebId.value
      );
      if (existingElement) {
        existingElement.classList.remove("show");
      }
      if (datatable.value) datatable.value.openAddEditModal = true;
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      loading.value = true;
      reqData = formData.value.edit ? reqData : { ...reqData };
      let message = formData.value.edit ? editMessage : addMessage;
      await LookupAPI.getInstance()
        .addLookup(reqData, formData.value.edit)
        .then(() => {
          toast.success(message);
          queryUpdate({
            ...localPreference.value,
            page: page.value - 1,
            size: rowsPerPage.value,
          });

          datatable.value?.VZFilter.getLookupData();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate({ ...localPreference.value });
    };
    const setLookupCloneData = async (data: any) => {
      let existingElement = document.querySelector(
        ".lookup-clone.show-clone-" + data.webId
      );
      if (existingElement) {
        existingElement.classList.remove("show");
      }
      openCloneModal.value = true;
      lookupObj.value = data;
      await nextTick();
      const modal = new Modal(
        document.getElementById(`kt_lookup_clone`) as HTMLElement
      );
      modal.show();
    };
    const resetWebId = (id) => {
      if (lookupWebId.value === undefined || lookupWebId.value === id) {
        lookupWebId.value = id;
      } else if (lookupWebId.value !== undefined) {
        let existingElement = document.querySelector(
          ".lookup-clone.show-clone-" + lookupWebId.value
        );
        if (existingElement) {
          existingElement.classList.remove("show");
        }
        lookupWebId.value = id;
      }
    };
    watch(
      showDeleteDialog,
      (showDeleteDialog) => {
        if (showDeleteDialog) {
          let existingElement = document.querySelector(
            ".lookup-clone.show-clone-" + lookupWebId.value
          );
          if (existingElement) {
            existingElement.classList.remove("show");
          }
        }
      },
      { deep: true }
    );
    return {
      bulkDelete,
      format,
      formData,
      tabs,
      tableData,
      tableHeader,
      tableFilter,
      sideDrawerTitle,
      sideDrawerIcon,
      showSearch,
      openDrawer,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      editLookup,
      tableRowClick,
      queryUpdate,
      submit,
      t,
      refreshList,
      permissionModel,
      hasPermission,
      datatable,
      showDeleteDialog,
      resetWebId,
      setLookupCloneData,
      openCloneModal,
      lookupObj,
    };
  },
});
